import React, { useEffect, useState } from 'react'
import { Input, Button, Select, Icon, Modal, Tooltip } from 'antd'

const { Option } = Select

const getSafeDialogue = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}

const getDialogueText = value => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (Array.isArray(value)) {
    return value.map(getDialogueText).filter(Boolean).join('\n')
  }
  if (typeof value === 'object') {
    return (
      value.text ||
      value.translated_text ||
      value.translatedText ||
      value.dialogue ||
      value.line ||
      value.content ||
      value.value ||
      ''
    )
  }
  return ''
}

const updateDialogueText = (currentValue, text) => {
  if (
    currentValue &&
    typeof currentValue === 'object' &&
    !Array.isArray(currentValue)
  ) {
    return { ...currentValue, text }
  }

  return text
}

const getCharacterDisplayName = (value, fallbackKey) => {
  if (typeof value === 'string' && value.trim()) return value
  if (value && typeof value === 'object') {
    return (
      value.name ||
      value.label ||
      value.current ||
      value.original ||
      fallbackKey.replace(/^__|__$/g, '')
    )
  }
  return fallbackKey.replace(/^__|__$/g, '')
}

const getCharacterKeyLabel = key => {
  const label = String(key || '').replace(/^__|__$/g, '')
  return label || key
}

// ─── DialogueEditor ───────────────────────────────────────────────────────────
// Props:
//   dialogue    = { [characterKey]: "dialogue text", ... }
//   onChange    = (newDialogue) => void
//   disabled    = bool
//   formData    = { characterMap, characterList, selectedCharacter, ... }
// ─────────────────────────────────────────────────────────────────────────────

const DialogueEditor = ({
  dialogue = {},
  onChange,
  disabled,
  formData = {}
}) => {
  const [localDialogue, setLocalDialogue] = useState(() =>
    getSafeDialogue(dialogue)
  )
  let characterMap = formData?.characterMap || {}
  if (typeof characterMap === 'string') {
    try {
      characterMap = JSON.parse(characterMap)
    } catch (e) {
      characterMap = {}
    }
  }

  const [isOpen, setIsOpen] = useState(false)
  const [isAddingModalOpen, setIsAddingModalOpen] = useState(false)
  const [selectedCharKey, setSelectedCharKey] = useState(undefined)
  const [newDialogueText, setNewDialogueText] = useState('')
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    setLocalDialogue(getSafeDialogue(dialogue))
  }, [dialogue])

  // ── Read helpers ──────────────────────────────────────────────────────────────
  const safeDialogue = getSafeDialogue(localDialogue)
  const entries = Object.entries(safeDialogue)
  const hasDialogue = entries.length > 0
  const allCharacters = Object.entries(characterMap)

  // ── Character image helper ────────────────────────────────────────────────────
  const getCharacterImage = key => {
    const characterList = formData?.characterList || []
    const selectedCharacters = formData?.selectedCharacter || []
    const mapping = selectedCharacters.find(
      m => m.original === key || m.current === key
    )
    const resolvedName = mapping
      ? mapping.current.replace(/^__|__$/g, '')
      : key.replace(/^__|__$/g, '')
    const characterId = mapping?.tag || null
    let character = null
    if (characterId) {
      character = characterList.find(c => c.character_id === characterId)
    }
    if (!character && resolvedName) {
      character = characterList.find(c => c.name === resolvedName)
    }
    return character?.attributes?.profile_pic || null
  }

  // ── Write helpers ─────────────────────────────────────────────────────────────
  function commitDialogueChange(nextDialogue) {
    setLocalDialogue(nextDialogue)
    if (typeof onChange === 'function') {
      onChange(nextDialogue)
    }
  }

  function updateDialogue(characterKey, text) {
    commitDialogueChange({
      ...safeDialogue,
      [characterKey]: updateDialogueText(safeDialogue[characterKey], text)
    })
  }

  function deleteDialogue(characterKey) {
    Modal.confirm({
      title: 'Delete Dialogue?',
      content:
        'Are you sure you want to remove this dialogue? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      centered: true,
      maskClosable: true,
      onOk() {
        const next = { ...safeDialogue }
        delete next[characterKey]
        commitDialogueChange(next)
      }
    })
  }

  function addDialogue() {
    if (!selectedCharKey || !newDialogueText?.trim()) return
    commitDialogueChange({
      ...safeDialogue,
      [selectedCharKey]: newDialogueText
    })
    setIsOpen(true)
    closeModal()
  }

  function closeModal() {
    setSelectedCharKey(undefined)
    setNewDialogueText('')
    setIsAddingModalOpen(false)
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* ── Header / toggle ── */}
      <div
        style={styles.header(isHovered)}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.headerTitle}>
          <Icon
            type="message"
            style={styles.headerIcon(hasDialogue && !isOpen)}
          />
          <span style={styles.headerLabel(hasDialogue && !isOpen)}>
            Shot Dialogue
          </span>
          {hasDialogue && !isOpen && (
            <span style={styles.badge}>{entries.length}</span>
          )}
        </div>
        <Icon
          type={isOpen ? 'up' : 'down'}
          style={styles.chevron(hasDialogue && !isOpen)}
        />
      </div>

      {/* ── Expanded body ── */}
      {isOpen && (
        <div style={styles.body}>
          {/* ── Empty state ── */}
          {!hasDialogue && (
            <div style={styles.emptyState}>
              <Icon type="message" style={{ fontSize: 16, color: '#d9d9d9' }} />
              <span style={styles.emptyText}>No dialogue added yet</span>
            </div>
          )}

          {/* ── Existing dialogue entries ── */}
          {hasDialogue && (
            <div style={styles.list}>
              {entries.map(([key, text], idx) => {
                const displayName = getCharacterDisplayName(
                  characterMap[key],
                  key
                )
                const characterKeyLabel = key
                const cleanCharacterKeyLabel = getCharacterKeyLabel(key)
                const hasResolvedCharacterName =
                  displayName &&
                  displayName !== key &&
                  displayName !== cleanCharacterKeyLabel
                const characterSubtitle = hasResolvedCharacterName
                  ? displayName
                  : ''
                const charImage = getCharacterImage(key)
                const initials = (characterSubtitle || cleanCharacterKeyLabel)
                  .split(' ')
                  .slice(0, 2)
                  .map(w => w[0])
                  .join('')
                  .toUpperCase()
                const isLast = idx === entries.length - 1
                const tooltipTitle = characterSubtitle
                  ? `${characterKeyLabel} - ${characterSubtitle}`
                  : characterKeyLabel

                return (
                  <div key={key} style={styles.entry(isLast)}>
                    {/* ── Avatar ── */}
                    <div style={styles.avatar}>
                      {charImage ? (
                        <img
                          src={charImage}
                          alt={displayName}
                          style={styles.avatarImg}
                        />
                      ) : (
                        <span style={styles.avatarInitials}>{initials}</span>
                      )}
                    </div>

                    {/* ── Content ── */}
                    <div style={styles.entryContent}>
                      <div style={styles.entryHeader}>
                        <Tooltip title={tooltipTitle} placement="topLeft">
                          <div style={styles.entryMeta}>
                            <span style={styles.charName}>
                              {characterKeyLabel}
                            </span>
                            {characterSubtitle && (
                              <span style={styles.charDetail}>
                                {characterSubtitle}
                              </span>
                            )}
                          </div>
                        </Tooltip>
                        {!disabled && (
                          <Icon
                            type="delete"
                            onClick={() => deleteDialogue(key)}
                            style={styles.deleteIcon}
                            title="Delete Dialogue"
                          />
                        )}
                      </div>
                      <Input.TextArea
                        value={getDialogueText(text)}
                        disabled={disabled}
                        onChange={e => updateDialogue(key, e.target.value)}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        style={styles.textarea}
                        placeholder="Enter dialogue..."
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* ── Add dialogue button ── */}
          {!disabled && (
            <Button
              type="dashed"
              icon="plus"
              size="small"
              block
              onClick={() => setIsAddingModalOpen(true)}
              style={styles.addButton}
            >
              Add Dialogue
            </Button>
          )}
        </div>
      )}

      {/* ── Add Dialogue Modal ── */}
      <Modal
        maskClosable={false}
        title="Add Character Dialogue"
        visible={isAddingModalOpen}
        onOk={addDialogue}
        onCancel={closeModal}
        okText="Add"
        cancelText="Cancel"
        okButtonProps={{
          disabled: !selectedCharKey || !newDialogueText?.trim()
        }}
        destroyOnClose
        centered
        width={420}
        bodyStyle={{ paddingTop: 16, paddingBottom: 16 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <div style={styles.modalLabel}>
              Select Character <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <Select
              showSearch
              placeholder="Search and select character…"
              optionFilterProp="children"
              value={selectedCharKey}
              onChange={setSelectedCharKey}
              style={{ width: '100%' }}
            >
              {allCharacters.map(([key, name]) => (
                <Option key={key} value={key}>
                  {getCharacterDisplayName(name, key)} ({key})
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <div style={styles.modalLabel}>
              Dialogue Text <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            <Input.TextArea
              placeholder="Enter dialogue here…"
              value={newDialogueText}
              onChange={e => setNewDialogueText(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 10 }}
              disabled={!selectedCharKey}
              style={{ fontSize: 13 }}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  container: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    border: '1px solid #f0f0f0',
    borderRadius: 12,
    overflow: 'hidden',
    marginTop: 10,
    backgroundColor: '#fafafa'
  },
  header: isHovered => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    gap: 8,
    cursor: 'pointer',
    background: isHovered ? '#e8e8e8' : '#f0f0f0',
    userSelect: 'none',
    borderBottom: '1px solid #e8e8e8',
    transition: 'all 0.2s ease'
  }),
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    flex: 1,
    overflow: 'hidden'
  },
  headerIcon: active => ({
    color: active ? '#1890ff' : '#8c8c8c',
    fontSize: 12,
    transition: 'color 0.2s ease'
  }),
  headerLabel: active => ({
    fontSize: 10,
    fontWeight: 700,
    color: active ? '#1890ff' : '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'color 0.2s ease'
  }),
  badge: {
    background: '#1890ff',
    color: '#fff',
    fontSize: 10,
    fontWeight: 600,
    height: 16,
    minWidth: 16,
    padding: '0 5px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 1px #fff',
    letterSpacing: 'normal',
    lineHeight: 1,
    flexShrink: 0
  },
  chevron: active => ({
    fontSize: 12,
    color: active ? '#1890ff' : '#bfbfbf',
    transition: 'transform 0.3s ease'
  }),
  body: {
    padding: '0 10px 10px',
    maxWidth: '100%',
    maxHeight: 150,
    minWidth: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    boxSizing: 'border-box'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 0',
    gap: 6
  },
  emptyText: {
    fontSize: 11,
    fontStyle: 'italic',
    color: '#bfbfbf'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden'
  },
  entry: isLast => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    paddingTop: 9,
    paddingBottom: 9,
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    borderBottom: isLast ? 'none' : '1px solid #f0f0f0'
  }),
  avatar: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: '1px solid #d9d9d9',
    overflow: 'hidden',
    flexShrink: 0,
    background: '#f0f2f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  avatarInitials: {
    fontSize: 11,
    fontWeight: 700,
    color: '#1890ff',
    letterSpacing: 0
  },
  entryContent: {
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  entryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    maxWidth: '100%',
    minWidth: 0
  },
  entryMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 2,
    overflow: 'hidden',
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
    cursor: 'help'
  },
  charName: {
    fontSize: 11,
    fontWeight: 600,
    color: '#1890ff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    maxWidth: '100%',
    lineHeight: 1.25
  },
  charDetail: {
    fontSize: 10,
    color: '#8c8c8c',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.2
  },
  deleteIcon: {
    color: '#bfbfbf',
    cursor: 'pointer',
    fontSize: 13,
    padding: '2px',
    transition: 'color 0.2s',
    flexShrink: 0
  },
  textarea: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    fontSize: 12,
    color: '#434343',
    resize: 'none',
    boxShadow: 'none',
    border: '1px solid #ebebeb',
    borderRadius: 6,
    background: '#fff',
    lineHeight: 1.5,
    overflowX: 'hidden'
  },
  addButton: {
    color: '#1890ff',
    borderColor: '#91d5ff',
    background: '#f0f7ff',
    height: 32,
    fontSize: 12,
    fontWeight: 600,
    marginTop: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 6,
    boxShadow: 'inset 0 0 0 1px rgba(24, 144, 255, 0.04)'
  },
  modalLabel: {
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 500,
    color: '#262626'
  }
}

export default DialogueEditor
